import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { autoScrollForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element';
import { token } from '@atlaskit/tokens';
import composeRefs from '@seznam/compose-react-refs';
import React, { forwardRef, type ReactNode, type Ref, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
const OuterContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  margin-left: 1px;
  height: 100%;
`;

const overheadSpaceStyle = css`
  margin-top: ${token('space.negative.100', '-8px')};
  padding-top: ${token('space.100', '8px')};
`;

// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
const InnerContainer = styled.div<{
  allowOverheadSpace?: boolean;
  heightIsLessThanContainer?: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  overflow-x: auto;
  overflow-y: ${(props) => (props.heightIsLessThanContainer ? 'hidden' : 'auto')};
  margin-right: 1px;
  // Extra space for column selectors on DatabaseContent
  ${(props) => props.allowOverheadSpace && overheadSpaceStyle}
`;

export interface ScrollableContainerWithShadowProps {
  className?: string;
  children: ReactNode;
  innerRef?: Ref<HTMLDivElement>;
  heightIsLessThanContainer?: boolean;
  allowOverheadSpace?: boolean;
}

const ScrollableContainer = forwardRef<
  HTMLDivElement,
  ScrollableContainerWithShadowProps
>(function ScrollableContainer(
  { className, children, innerRef, heightIsLessThanContainer, allowOverheadSpace },
  ref
) {
  const outerContainerRef = useRef<HTMLDivElement>(null);
  const innerContainerRef = useRef<HTMLDivElement>(null);

  // Enables auto scrolling for table fields
  useEffect(() => {
    if (!innerContainerRef.current) {
      return;
    }
    return combine(
      autoScrollForElements({
        element: innerContainerRef.current,
        getConfiguration: () => ({
          maxScrollSpeed: 'standard',
        }),
      })
    );
  }, []);

  return (
    <OuterContainer
      data-testid="scrollable-outer-container"
      ref={composeRefs(outerContainerRef, ref)}
      className={className}
    >
      <InnerContainer
        id={'scrollable-inner-container'}
        data-testid="scrollable-inner-container"
        allowOverheadSpace={allowOverheadSpace}
        ref={composeRefs(innerContainerRef, innerRef)}
        heightIsLessThanContainer={heightIsLessThanContainer}
      >
        {children}
      </InnerContainer>
    </OuterContainer>
  );
});

export default ScrollableContainer;
